// src/components/Dashboard/Dashboard.js
import React from 'react';
import { RefreshCw, Loader } from 'lucide-react';
import BusinessMetrics from './BusinessMetrics';
import PerformanceCharts from './PerformanceCharts';
import QuickActions from './QuickActions';
import AIBusinessAssistant from '../AI/AIBusinessAssistant';
import FileDropZone from '../FileUpload/FileDropZone';
import { useAetherisStatus } from '../../hooks/useAetherisStatus';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { theme, rgba } from '../../theme';
import { rateTileProps } from '../../hooks/businessDataShape';

// businessData/isLoading/error/refreshData/derivedMetrics now come from App.js's own
// single useBusinessData() call rather than this component calling the hook again
// itself - this used to run its own independent fetch/30s-poll/visibility-listener
// loop in parallel with App.js's, doubling real requests to /api/business-data
// whenever the dashboard was open (found live 2026-09-17).
const Dashboard = ({
  userName,
  companyName,
  onModuleChange,
  onNavigate,
  onChatOpen,
  onFileAnalyzed,
  businessData,
  isLoading,
  error,
  refreshData,
  derivedMetrics
}) => {
  const { aetherisStatus } = useAetherisStatus();

  // "0 AI Cores Online" when the status check fails reads like zero cores
  // exist, not that all of them are just temporarily unreachable - say so
  // explicitly instead, using the last known total when we have one.
  // "initializing" (briefly true right after a backend restart, before
  // aetherisSystem is assigned) is a real, different state from an actual
  // fetch failure - don't call that "offline" either.
  const coresOnline = aetherisStatus?.status === 'online';
  const coresInitializing = aetherisStatus?.status === 'initializing';
  const coresBadgeText = !aetherisStatus
    ? '— AI Cores'
    : coresOnline
      ? `✅ ${aetherisStatus.cores} AI Cores Online`
      : coresInitializing
        ? '🔄 AI Cores Starting Up'
        : `⚠️ AI Cores Offline${aetherisStatus.totalCores ? ` (${aetherisStatus.totalCores} unreachable)` : ''}`;
  const coresBadgeColor = coresOnline ? '#22c55e' : coresInitializing ? '#f59e0b' : (aetherisStatus ? '#ef4444' : '#94a3b8');

  if (isLoading) {
    return (
      <div style={{
        padding: '24px',
        background: `linear-gradient(135deg, ${rgba(theme.colors.primaryRgb, 0.1)}, ${rgba(theme.colors.accentRgb, 0.1)})`,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Loader size={48} style={{ color: theme.colors.primary, marginBottom: '16px' }} className="animate-spin" />
          <p style={{ color: theme.colors.textOnPageBackground, fontSize: '18px' }}>Loading real business data...</p>
          {error && (
            <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>
              Error: {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  const rateTiles = rateTileProps(businessData.analytics);

  return (
    <div style={{
      padding: '24px',
      background: `linear-gradient(135deg, ${rgba(theme.colors.primaryRgb, 0.1)}, ${rgba(theme.colors.accentRgb, 0.1)})`,
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '42px',
          fontWeight: 'bold',
          color: theme.colors.primary,
          marginBottom: '8px',
          textShadow: `0 0 30px ${rgba(theme.colors.primaryRgb, 0.6)}`
        }}>
          🧠 Sentari OS
        </h1>
        <p style={{ color: theme.colors.textOnPageBackground, fontSize: '20px', marginBottom: '16px' }}>
          AI-Powered Business Intelligence Platform
        </p>
        
        {/* Status Indicators */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            padding: '8px 16px',
            background: `${coresBadgeColor}33`,
            border: `1px solid ${coresBadgeColor}4D`,
            borderRadius: '20px',
            color: coresBadgeColor,
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {coresBadgeText}
          </div>
          
          <div style={{
            padding: '8px 16px',
            background: rgba(theme.colors.primaryRgb, 0.2),
            border: `1px solid ${rgba(theme.colors.primaryRgb, 0.3)}`,
            borderRadius: '20px',
            color: theme.colors.primary,
            fontSize: '12px',
            fontWeight: '600'
          }}>
            📦 {derivedMetrics.totalModules} Modules Active
          </div>

          {userName && (
            <div style={{
              padding: '8px 16px',
              background: rgba(theme.colors.accentRgb, 0.2),
              border: `1px solid ${rgba(theme.colors.accentRgb, 0.3)}`,
              borderRadius: '20px',
              color: theme.colors.accent,
              fontSize: '12px',
              fontWeight: '600'
            }}>
              👤 Welcome, {userName}!
            </div>
          )}
          
          {companyName && (
            <div style={{ 
              padding: '8px 16px',
              background: 'rgba(245, 158, 11, 0.2)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '20px',
              color: '#f59e0b',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              🏢 {companyName}
            </div>
          )}
        </div>
      </div>

      {/* Business Metrics */}
      <BusinessMetrics data={businessData} onRefresh={refreshData} onNavigate={onNavigate} />

      {/* AI Business Assistant */}
      <AIBusinessAssistant 
        userName={userName}
        companyName={companyName}
        onChatOpen={onChatOpen}
      />

      {/* File Drop Zone */}
      <FileDropZone onFileAnalyzed={onFileAnalyzed} />

      {/* Performance Charts */}
      <PerformanceCharts data={businessData} />

      {/* Quick Actions */}
      <QuickActions 
        onModuleChange={onModuleChange}
        onChatOpen={onChatOpen}
        onRefreshData={refreshData}
      />

      {/* Real-Time Intelligence */}
      {businessData.analytics && (
        <div style={{
          background: rgba(theme.colors.tileBackgroundRgb, 0.8),
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '12px',
          padding: '20px',
          marginTop: '32px'
        }}>
          <h3 style={{ 
            color: '#22c55e', 
            fontSize: '18px', 
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📊 Real-Time Business Intelligence
            <div style={{
              fontSize: '10px',
              padding: '2px 6px',
              background: 'rgba(34, 197, 94, 0.2)',
              borderRadius: '8px',
              marginLeft: 'auto'
            }}>
              LIVE DATA
            </div>
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
            gap: '16px' 
          }}>
            <div
              onClick={() => onNavigate?.('team')}
              style={{
                padding: '12px',
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                cursor: onNavigate ? 'pointer' : 'default'
              }}>
              <div style={{ color: '#f59e0b', fontSize: '12px', fontWeight: '600' }}>
                EMPLOYEES ON LEAVE
              </div>
              <div style={{ color: '#e2e8f0', fontSize: '20px', fontWeight: 'bold' }}>
                {businessData.analytics.employeesOnLeave || 0}
              </div>
            </div>

            <div
              onClick={() => onNavigate?.('financial-reports')}
              style={{
                padding: '12px',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                cursor: onNavigate ? 'pointer' : 'default'
              }}>
              <div style={{ color: '#22c55e', fontSize: '12px', fontWeight: '600' }}>
                {rateTiles.label}
              </div>
              <div style={{ color: '#e2e8f0', fontSize: '20px', fontWeight: 'bold' }}>
                {rateTiles.bySite.value !== null ? formatCurrency(rateTiles.bySite.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                <span style={{ color: '#6b7280', fontSize: '11px', fontWeight: '400' }}> {rateTiles.bySite.label}</span>
              </div>
              <div style={{ color: '#e2e8f0', fontSize: '16px', fontWeight: 'bold' }}>
                {rateTiles.byHours.value !== null ? formatCurrency(rateTiles.byHours.value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                <span style={{ color: '#6b7280', fontSize: '11px', fontWeight: '400' }}> {rateTiles.byHours.label}</span>
              </div>
              <div style={{ color: '#6b7280', fontSize: '11px', marginTop: '2px' }}>
                {rateTiles.sub}
              </div>
            </div>

            <div
              onClick={() => onNavigate?.('financial-reports')}
              style={{
                padding: '12px',
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '8px',
                cursor: onNavigate ? 'pointer' : 'default'
              }}>
              <div style={{ color: '#a855f7', fontSize: '12px', fontWeight: '600' }}>
                AVG EMPLOYEE PAY
              </div>
              <div style={{ color: '#e2e8f0', fontSize: '20px', fontWeight: 'bold' }}>
                {businessData.analytics.avgEmployeePay !== null
                  ? formatCurrency(businessData.analytics.avgEmployeePay, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : '—'}
              </div>
              <div style={{ color: '#6b7280', fontSize: '11px', marginTop: '2px' }}>
                {businessData.analytics.employeePayUnconfirmedCount > 0
                  ? `${businessData.analytics.employeePayConfirmedCount} confirmed, ${businessData.analytics.employeePayUnconfirmedCount} unconfirmed rate`
                  : `${businessData.analytics.employeePayConfirmedCount} active employee(s), company-wide`}
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: rgba(theme.colors.primaryRgb, 0.1),
              border: `1px solid ${rgba(theme.colors.primaryRgb, 0.3)}`,
              borderRadius: '8px'
            }}>
              <div style={{ color: theme.colors.primary, fontSize: '12px', fontWeight: '600' }}>
                LAST UPDATED
              </div>
              <div style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: 'bold' }}>
                {businessData.lastUpdated
                  ? formatDate(businessData.lastUpdated, { includeTime: true, timeStyle: 'short' })
                  : 'Never'}
              </div>
            </div>

            {businessData.analytics.grossProfit > 0 && (
              <div
                onClick={() => onNavigate?.('financial-reports')}
                style={{
                  padding: '12px',
                  background: rgba(theme.colors.accentRgb, 0.1),
                  border: `1px solid ${rgba(theme.colors.accentRgb, 0.3)}`,
                  borderRadius: '8px',
                  cursor: onNavigate ? 'pointer' : 'default'
                }}>
                <div style={{ color: theme.colors.accent, fontSize: '12px', fontWeight: '600' }}>
                  GROSS PROFIT
                </div>
                <div style={{ color: '#e2e8f0', fontSize: '20px', fontWeight: 'bold' }}>
                  {formatCurrency(businessData.analytics.grossProfit)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 
